import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './BookDemo.css';
import coverImg from '../../assets/cover_light.png';

const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda',
  'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium',
  'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
  'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
  'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic',
  'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica',
  'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
  'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia',
  'Eswatini', 'Ethiopia',
  'Fiji', 'Finland', 'France',
  'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada',
  'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
  'Haiti', 'Honduras', 'Hungary',
  'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Jamaica', 'Japan', 'Jordan',
  'Kazakhstan', 'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein',
  'Lithuania', 'Luxembourg',
  'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia',
  'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
  'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger',
  'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman',
  'Pakistan', 'Palau', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru',
  'Philippines', 'Poland', 'Portugal',
  'Qatar',
  'Romania', 'Russia', 'Rwanda',
  'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines',
  'Samoa', 'San Marino', 'São Tomé and Príncipe', 'Saudi Arabia', 'Senegal',
  'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia',
  'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan',
  'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
  'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga',
  'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States',
  'Uruguay', 'Uzbekistan',
  'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
  'Yemen',
  'Zambia', 'Zimbabwe',
];

const sanitize = (str) => str.replace(/[<>"'&]/g, '');

const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'live.com', 'msn.com', 'me.com', 'inbox.com',
  'fastmail.com', 'tutanota.com', 'hushmail.com', 'mailfence.com',
  'yahoo.co.uk', 'yahoo.co.in', 'hotmail.co.uk', 'googlemail.com',
  'rocketmail.com', 'rediffmail.com', 'qq.com', '163.com', '126.com',
];

const isBusinessEmail = (email) => {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return false;
  return !FREE_EMAIL_DOMAINS.includes(domain);
};

const BookDemo = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    jobTitle: '',
    country: '',
    howHeard: '',
    message: '',
    agreeComms: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');
  const recaptchaRef = useRef(null);

  useEffect(() => {
    const renderRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.enterprise && recaptchaRef.current && !recaptchaRef.current.hasChildNodes()) {
        window.grecaptcha.enterprise.render(recaptchaRef.current, {
          sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY,
          action: 'book_demo',
          callback: (token) => {
            setRecaptchaToken(token);
            setRecaptchaError('');
          },
          'expired-callback': () => {
            setRecaptchaToken(null);
          },
        });
      }
    };
    // reCAPTCHA script may load after component mounts
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      window.grecaptcha.enterprise.ready(renderRecaptcha);
    } else {
      const interval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval);
          window.grecaptcha.enterprise.ready(renderRecaptcha);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : sanitize(value) });
    if (name === 'email') {
      const sanitized = sanitize(value);
      if (sanitized && sanitized.includes('@') && !isBusinessEmail(sanitized)) {
        setEmailError('Please use a business email address.');
      } else {
        setEmailError('');
      }
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!isBusinessEmail(form.email)) {
      setEmailError('Please use a business email address.');
      return;
    }
    setSubmitting(true);
    try {
      if (!recaptchaToken) {
        setRecaptchaError('Please complete the reCAPTCHA challenge.');
        setSubmitting(false);
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || 'https://bltzbackend.vercel.app';
      const response = await fetch(`${apiBase}/api/demo/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recaptchaToken,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          phone: form.phone,
          organization: form.organization,
          jobTitle: form.jobTitle,
          country: form.country,
          howHeard: form.howHeard,
          message: form.message,
          agreeComms: form.agreeComms,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Submission failed');
      }

      setSubmitted(true);
    } catch (err) {
      console.error('Demo submission error:', err.message);
      // Still show success to prevent info leaking to bots
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  }, [form, recaptchaToken]);

  return (
    <>
      <section className="main-hero demo-hero">
        <div className="hero-gif-fade-left"></div>
        <div className="hero-gif-fade-right"></div>
        <h1 className="hero-page-title">Book a Demo</h1>
      </section>

      <section className="demo-content">
        <div className="demo-layout content-width">
          <div className="demo-left">
            <h2 className="demo-cta-heading">
              See Bltz AI in Action
            </h2>
            <p className="demo-cta-description">
              Schedule a demo to learn how Bltz AI provides visibility, protection, and governance for AI systems. We'll show how Runtime Shield, Purple Team, and Workspace work together to secure AI applications and agents.
            </p>

            <div className="demo-video-wrap">
              {playing ? (
                <iframe
                  className="demo-video-iframe"
                  src="https://www.youtube.com/embed/jDoTa0Hbu_w?autoplay=1"
                  title="Bltz AI Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="demo-video-thumb" onClick={() => setPlaying(true)}>
                  <img src={coverImg} alt="Watch Bltz AI demo" className="demo-video-cover" />
                  <button className="demo-video-play" aria-label="Play video">
                    <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="26,20 26,44 46,32" fill="#fff" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="demo-right">
            {submitted ? (
              <div className="demo-success">
                <h3>Thank you!</h3>
                <p>We've received your request and will be in touch shortly.</p>
                <button className="demo-explore-btn" onClick={() => navigate('/')}>Continue Exploring Bltz Platform →</button>
              </div>
            ) : (
              <form className="demo-form" onSubmit={handleSubmit}>
                <div className="demo-form-row">
                  <div className="demo-field">
                    <label>First name*</label>
                    <input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="demo-field">
                    <label>Last name*</label>
                    <input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="demo-field">
                  <label>Business Email*</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    maxLength={254}
                    autoComplete="email"
                    className={emailError ? 'input-error' : ''}
                  />
                  {emailError && <span className="demo-field-error">{emailError}</span>}
                </div>

                <div className="demo-field">
                  <label>Phone number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    maxLength={20}
                    autoComplete="tel"
                  />
                </div>

                <div className="demo-form-row">
                  <div className="demo-field">
                    <label>Company Name*</label>
                    <input
                      type="text"
                      name="organization"
                      value={form.organization}
                      onChange={handleChange}
                      required
                      maxLength={200}
                      autoComplete="organization"
                    />
                  </div>
                  <div className="demo-field">
                    <label>Job title*</label>
                    <input
                      type="text"
                      name="jobTitle"
                      value={form.jobTitle}
                      onChange={handleChange}
                      required
                      maxLength={150}
                      autoComplete="organization-title"
                    />
                  </div>
                </div>

                <div className="demo-field">
                  <label>Country/Region*</label>
                  <select
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Please Select</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="demo-field">
                  <label>How did you hear about us?*</label>
                  <textarea
                    name="howHeard"
                    value={form.howHeard}
                    onChange={handleChange}
                    required
                    rows={4}
                    maxLength={1000}
                  />
                </div>

                <div className="demo-field">
                  <label>Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    maxLength={2000}
                  />
                </div>

                <label className="demo-checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeComms"
                    checked={form.agreeComms}
                    onChange={handleChange}
                  />
                  <span>I agree to receive communications from Bltz AI.</span>
                </label>

                <p className="demo-legal-text">
                  By submitting this form, you agree to Bltz AI's{' '}
                  <Link to="/privacy">Terms of Use</Link> and acknowledge our{' '}
                  <Link to="/privacy">Privacy Policy</Link>.
                </p>

                <div className="demo-recaptcha-wrap">
                  <div ref={recaptchaRef}></div>
                  {recaptchaError && <span className="demo-field-error">{recaptchaError}</span>}
                </div>

                <button type="submit" className="demo-submit-btn" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
};

export default BookDemo;
